import { supabase } from "@/integrations/supabase/client";

interface ClaimResult {
  success: boolean;
  message: string;
  needsAuth: boolean;
  alreadyClaimed?: "active" | "pending";
}

/**
 * Shared claim logic for parent/guardian claiming a player.
 * Returns { success, message, needsAuth } so callers can handle redirect or toast.
 */
export async function claimPlayer(
  playerId: string,
  teamId: string,
  playerName: string
): Promise<ClaimResult> {
  // Wait briefly for auth to resolve (handles race condition on page load)
  let session = (await supabase.auth.getSession()).data.session;
  if (!session) {
    // Give auth state a moment to settle (e.g. after redirect)
    await new Promise((r) => setTimeout(r, 500));
    session = (await supabase.auth.getSession()).data.session;
  }

  if (!session) {
    // Store intent for post-login flow
    sessionStorage.setItem(
      "claimIntent",
      JSON.stringify({ playerId, teamId, returnUrl: window.location.pathname + window.location.search + window.location.hash })
    );
    return { success: false, message: "Please sign in to claim your child", needsAuth: true };
  }

  try {
    const { data, error } = await supabase.functions.invoke("claim-player-guardian", {
      body: JSON.stringify({ playerId, teamId }),
    });

    if (error) {
      console.error("Claim error:", error);
      // Try to extract the real response body from the edge function
      let body: any = null;
      try {
        if (error.context && typeof error.context.json === "function") {
          body = await error.context.json();
        }
      } catch {
        // ignore parse failure
      }

      // If the edge function returned a structured response (e.g. 409 already claimed)
      if (body) {
        const status = body.status || body.guardian_status;
        if (status === "active" || status === "pending") {
          return {
            success: false,
            message: body.error || body.message || "Already claimed",
            needsAuth: false,
            alreadyClaimed: status as "active" | "pending",
          };
        }
        return { success: false, message: body.error || body.message || "Couldn't submit claim — please try again", needsAuth: false };
      }

      return { success: false, message: "Couldn't submit claim — please try again", needsAuth: false };
    }

    if (data?.success) {
      return {
        success: true,
        message: data.message || `Claim submitted for ${playerName}`,
        needsAuth: false,
      };
    }

    return { success: false, message: data?.error || "Couldn't submit claim", needsAuth: false };
  } catch (err) {
    console.error("Claim error:", err);
    return { success: false, message: "Something went wrong", needsAuth: false };
  }
}
